import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Radio } from "../tailwind/form";
import { Modal } from "../tailwind/modal/index";

const WIRE_TRANSFER = "WIRE_TRANSFER";
const CREDIT_CARD = "CREDIT_CARD";
const CREDIT_CARD_SURCHARGE = "3.2%";

const PaymentTypeSelector = ({ team, paymentMethods, teamIsSelfServed, layout }) => {
  const [paymentType, setPaymentType] = useState(
    team.payment_type || WIRE_TRANSFER
  );
  const [billingButtonDisabled, setBillingButtonDisabled] = useState();
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showConfirmChangeModal, setShowConfirmChangeModal] = useState(false);
  const [pendingPaymentType, setPendingPaymentType] = useState(null);
  const [invoicing, setInvoicing] = useState({});

  useEffect(() => {
    setInvoicing(window.invoicing || {});
  }, [window]);

  // const getQueryParams = () => {
  //   const params = new URLSearchParams(window.location.search);
  //   return Object.fromEntries(params.entries());
  // };

  const removeQueryParam = (param) => {
    const url = new URL(window.location);
    url.searchParams.delete(param);
    // Update the URL without reloading the page
    window.history.replaceState({}, "", url.href);
  };

  const updatePaymentType = (nextPaymentType) => {
    return axios
    .put('/billing/payment-types', {payment_type: nextPaymentType})
    .then((response) => {
      if (response.data.url !== null) {
        window.location = response.data.url;
      }
      return response;
    });
  };

  const handleQueryParams = () => {
    // const queryParams = getQueryParams();
    // if (queryParams.status === "cancelled") {
    //   setPaymentType(WIRE_TRANSFER);
    //   updatePaymentType(WIRE_TRANSFER);
    // }
    removeQueryParam("status");
  };

  useEffect(() => {
    handleQueryParams();
  }, []);

  const closePaymentDetailsModal = () => {
    setShowPaymentDetailsModal(false);
  };

  const handleChange = (event) => {
    if (event.target.name === "payment_type") {
      const selectedPaymentType = event.target.value;
      const currentPaymentType = paymentType;

      // If changing from Credit Card to Wire, show confirmation modal
      if (currentPaymentType === CREDIT_CARD && selectedPaymentType === WIRE_TRANSFER) {
        setPendingPaymentType(selectedPaymentType);
        setShowConfirmChangeModal(true);
      } else {
        // Direct change for other cases
        setPaymentType(selectedPaymentType);
        updatePaymentType(selectedPaymentType);
      }
    }
  };

  const handleConfirmChange = () => {
    if (pendingPaymentType) {
      setPaymentType(pendingPaymentType);
      updatePaymentType(pendingPaymentType);
      setShowConfirmChangeModal(false);
      setPendingPaymentType(null);
    }
  };

  const handleCancelChange = () => {
    setShowConfirmChangeModal(false);
    setPendingPaymentType(null);
  };

  const handleCardButtonClick = async () => {
    setBillingButtonDisabled(true);
    const response = await axios.post(`/billing/billing-portal`);

    window.location = response.data.url;
  };

  return (
    <Fragment>
      <div className={layout === 'row' ? 'tw-flex tw-flex-row tw-gap-8 tw-items-start' : ''}>
        <form id="paymentForm" onChange={handleChange}>
          <fieldset className={`tw-border-0 tw-p-0 ${layout === 'row' ? 'tw-flex tw-flex-row tw-gap-2' : ''}`}>
            <legend className="tw-font-medium tw-mb-2">Payment Type</legend>
            {!teamIsSelfServed ? (
              <div>
                <Radio
                  className="tw-mr-3"
                  name="payment_type"
                  value={WIRE_TRANSFER}
                  label="Wire Transfer"
                  id="wire_transfer"
                  checked={paymentType === WIRE_TRANSFER}
                  onChange={handleChange}
                />
              </div>
            ) : null}
            <div>
              <Radio
                name="payment_type"
                value={CREDIT_CARD}
                id="credit_card"
                label="Credit Card"
                checked={paymentType === CREDIT_CARD}
                onChange={handleChange}
              />
            </div>
          </fieldset>
        </form>
        {paymentType === CREDIT_CARD && paymentMethods.cardsAvailable === false && (
          <div className="tw-mt-3">
            <p>You must have at least one card added to your account.</p>
            <button
              disabled={billingButtonDisabled}
              onClick={handleCardButtonClick}
              className="btn button-alt"
            >
              Add Card
            </button>
          </div>
        )}
        {paymentType === CREDIT_CARD && paymentMethods.cardsAvailable && (
          <div className="tw-mt-3">
            <button
              disabled={billingButtonDisabled}
              onClick={handleCardButtonClick}
              className="btn button-alt"
            >
              Manage cards
            </button>
            <p>There is a {CREDIT_CARD_SURCHARGE} surcharge for card payments.</p>
          </div>
        )}
        {paymentType === WIRE_TRANSFER && (
          <div className="tw-mt-3">
            <button
              onClick={() => setShowPaymentDetailsModal(true)}
              className="btn button-alt"
            >
              View Payment Details
            </button>
            <p>This information is also available on the invoice sent to your registered email.</p>
          </div>
        )}
      </div>
      <Modal
        isOpen={showConfirmChangeModal}
        onClose={handleCancelChange}
        contentLabel="Confirm Payment Method Change"
        heading={<h1 className="tw-my-0 tw-text-3xl">Confirm Payment Method Change</h1>}
        customContentStyle={{
          maxWidth: "35rem",
          maxHeight: "20rem",
          marginLeft: "auto",
          marginRight: "auto",
          background: "rgba(243, 244, 246, 0.1)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          borderRadius: "1rem",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          padding: "1.5rem",
          color: "#111827",
        }}
        customTextColor="tw-text-gray-900"
      >
        <p className="tw-mb-6">
          Are you sure you want to change your payment method from Credit Card to Wire Transfer? 
          This change will be sent to our customer service team for follow-up.
        </p>
        <div className="tw-flex tw-gap-3 tw-justify-end">
          <button
            onClick={handleCancelChange}
            className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg
                     tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmChange}
            className="tw-px-4 tw-py-2 tw-bg-theme-teal tw-text-white tw-rounded-lg
                     hover:tw-bg-theme-teal-dark tw-transition-colors tw-font-semibold"
          >
            Confirm Change to Wire
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={showPaymentDetailsModal}
        onClose={closePaymentDetailsModal}
        contentLabel="Payment details"
        heading={<h1 className="tw-my-0 tw-text-3xl">Payment details</h1>}
      >
        <p>
          <span className="tw-font-bold">Company Name:</span>{" "}
          {invoicing.company_name}
        </p>
        <p>
          <span className="tw-font-bold">Bank Name:</span> {invoicing.bank_name}
        </p>
        <p>
          <span className="tw-font-bold">SWIFT/BIC Code:</span>{" "}
          {invoicing.swift_bic}
        </p>
        <p>
          <span className="tw-font-bold">Bank Account Number:</span>{" "}
          {invoicing.account_number}
        </p>
        <p>
          <span className="tw-font-bold">IBAN:</span> {invoicing.iban}
        </p>
      </Modal>
    </Fragment>
  );
};

PaymentTypeSelector.propTypes = {
  team: PropTypes.object.isRequired,
  paymentMethods: PropTypes.shape({
    apiError: PropTypes.bool.isRequired,
    cardsAvailable: PropTypes.bool.isRequired,
  }).isRequired,
  teamIsSelfServed: PropTypes.bool.isRequired,
  layout: PropTypes.string.isRequired,
};

export default PaymentTypeSelector;
